import { Component, ErrorInfo, ReactNode } from 'react'
import './ErrorBoundary.css'

export interface ErrorBoundaryProps {
  /** Children to render */
  children: ReactNode
  /** Custom fallback UI (optional) */
  fallback?: ReactNode
  /** Error handler callback (optional) */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

export interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary component to catch React errors
 * Derek-friendly: clear message, simple retry action
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null })
  }

  handleGoHome = (): void => {
    window.location.href = '/'
  }

  handleRefresh = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default Derek-friendly error UI
      return (
        <div className="ps-error-boundary" role="alert">
          <div className="ps-error-boundary__content">
            <div className="ps-error-boundary__icon" aria-hidden="true">
              !
            </div>
            <h1 className="ps-error-boundary__title">Something went wrong</h1>
            <p className="ps-error-boundary__message">
              We&apos;re sorry, but something unexpected happened. Please try
              again or contact support if the problem continues.
            </p>

            <div className="ps-error-boundary__actions">
              <button
                type="button"
                className="ps-error-boundary__btn ps-error-boundary__btn--primary"
                onClick={this.handleRetry}
              >
                Try Again
              </button>
              <button
                type="button"
                className="ps-error-boundary__btn ps-error-boundary__btn--secondary"
                onClick={this.handleRefresh}
              >
                Refresh Page
              </button>
              <button
                type="button"
                className="ps-error-boundary__btn ps-error-boundary__btn--ghost"
                onClick={this.handleGoHome}
              >
                Go to Home
              </button>
            </div>

            {/* Show error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <details className="ps-error-boundary__details">
                <summary>Error Details (Development Only)</summary>
                <pre>{this.state.error.message}</pre>
                <pre>{this.state.error.stack}</pre>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
